'use client';
import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const testimonials = [
{
  name: 'Jasmine Okafor',
  title: 'Colorist, 2 years post-license',
  salon: 'Prism Studio, Atlanta GA',
  quote: 'I passed my advanced color certification on the first try. The overhead angle on the balayage module finally showed me what my instructor was describing. It clicked in 20 minutes.',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1c1dce958-1772074776478.png",
  alt: 'Jasmine Okafor, colorist with natural hair and warm smile in salon setting',
  cert: 'Advanced Color Cert.'
},
{
  name: 'Marcus Tran',
  title: 'Salon owner, 8 years',
  salon: 'Eleven Studio, Houston TX',
  quote: 'I onboarded three new hires using Academy. Saved me a full week of chair-side training. They came in knowing the foil system. I just had to supervise their first real client.',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1fcab2f53-1772182366296.png",
  alt: 'Marcus Tran, salon owner in black apron standing confidently in modern salon',
  cert: 'Salon Owner Track'
}];


const certPartners = [
{ name: 'CA State Board', abbr: 'CA BOARD' },
{ name: 'TX State Board', abbr: 'TX BOARD' },
{ name: 'NCEA', abbr: 'NCEA' },
{ name: 'CIDESCO', abbr: 'CIDESCO' },
{ name: 'Pivot Point', abbr: 'PIVOT PT.' },
{ name: 'ABMP', abbr: 'ABMP' }];


export default function CertificationSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal, .reveal-left, .reveal-right').forEach((el) => {
              el.classList.add('visible');
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="certification" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="section-divider mb-24" />
      <div className="absolute inset-0 pointer-events-none">
        <div className="blob-sky absolute bottom-[10%] left-[-5%] w-[350px] h-[350px] rounded-full opacity-25" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div className="reveal text-center mb-16">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-sky-DEFAULT mb-3 block">§ Certification</span>
          <h2 className="font-sans text-4xl sm:text-5xl font-extrabold text-frost tracking-tight leading-tight mb-4">
            Credentials that open doors.
          </h2>
          <p className="text-chrome text-base font-light max-w-xl mx-auto">
            Every Academy completion certificate is recognized by state boards and continuing-education accreditors across the US.
          </p>
        </div>

        {/* Cert partners */}
        <div className="reveal glass-card rounded-2xl p-6 sm:p-8 mb-12">
          <div className="text-xs font-bold uppercase tracking-widest text-chrome-dim mb-6 text-center">
            Recognized by
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
            {certPartners?.map((p, i) =>
            <div key={i} className="flex items-center gap-2 group">
                <div
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(86,204,242,0.1)', border: '1px solid rgba(86,204,242,0.2)' }}>
                
                  <span className="text-[8px] font-bold text-sky-DEFAULT">✦</span>
                </div>
                <span className="text-xs font-bold tracking-widest uppercase text-chrome-dim group-hover:text-frost transition-colors">
                  {p?.abbr}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Asymmetric layout: credential info + testimonials */}
        <div className="grid lg:grid-cols-5 gap-8 items-start">
          {/* Left — credential spec (2 cols) */}
          <div className="lg:col-span-2 reveal-left space-y-5">
            <div className="glass-card rounded-2xl p-6">
              <h3 className="text-frost font-bold text-lg mb-4">What you earn</h3>
              <div className="space-y-4">
                {[
                { icon: '🏆', title: 'Completion Certificate', desc: 'PDF + shareable digital badge per module' },
                { icon: '📋', title: 'CEU Credits', desc: 'Accepted for license renewal in 38 states' },
                { icon: '🔗', title: 'LinkedIn Credential', desc: 'One-click add to your professional profile' },
                { icon: '💼', title: 'Salon Portfolio PDF', desc: 'Auto-generated from your completed modules' }]?.
                map((item, i) =>
                <div key={i} className="flex items-start gap-3">
                    <span className="text-xl mt-0.5">{item?.icon}</span>
                    <div>
                      <div className="text-frost text-sm font-semibold">{item?.title}</div>
                      <div className="text-chrome-dim text-xs">{item?.desc}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Exam pass rate */}
            <div className="glass-card-bright rounded-2xl p-6 text-center">
              <div className="text-5xl font-extrabold gradient-text-sky mb-2">98%</div>
              <div className="text-frost text-sm font-semibold mb-1">First-attempt pass rate</div>
              <div className="text-chrome-dim text-xs">on state board practical exams</div>
              <div className="text-chrome-dim text-[10px] mt-3 font-light">Among students who completed ≥3 modules</div>
            </div>
          </div>

          {/* Right — testimonials (3 cols) */}
          <div className="lg:col-span-3 reveal-right space-y-5">
            {testimonials?.map((t, i) =>
            <div key={i} className="glass-card rounded-2xl p-6 sm:p-8 glow-hover transition-all duration-500">
                {/* Stars */}
                <div className="flex items-center gap-1 mb-4">
                  {[1, 2, 3, 4, 5]?.map((s) =>
                <svg key={s} className="w-4 h-4 text-sky-DEFAULT" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                )}
                  <span className="ml-2 text-xs font-bold text-periwinkle-DEFAULT uppercase tracking-wide">{t?.cert}</span>
                </div>

                <blockquote className="text-chrome text-sm sm:text-base font-light leading-relaxed mb-5 italic">
                  "{t?.quote}"
                </blockquote>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-sky-DEFAULT/30 shrink-0">
                    <AppImage src={t?.img} alt={t?.alt} width={40} height={40} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <div className="text-frost text-sm font-bold">{t?.name}</div>
                    <div className="text-chrome-dim text-xs">{t?.title}</div>
                    <div className="text-sky-DEFAULT text-[10px] font-semibold">{t?.salon}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

}